import { useEffect, useRef, useState } from 'react';
import './DeleteOrganizationModal.css';

interface DeleteOrganizationModalProps {
  isOpen: boolean;
  orgName: string;
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

// Inner modal content, re-mounted via key on each open to reset state
function DeleteOrganizationModalContent({
  orgName,
  loading,
  onConfirm,
  onCancel,
}: Omit<DeleteOrganizationModalProps, 'isOpen'> & { loading: boolean }) {
  const [inputValue, setInputValue] = useState('');
  const [showError, setShowError] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const mouseDownInsideContent = useRef(false);

  useEffect(() => {
    // Auto-focus input after modal mounts
    const timer = setTimeout(() => {
      inputRef.current?.focus();
    }, 150);
    return () => clearTimeout(timer);
  }, []);

  const isMatch = inputValue === orgName;

  const handleConfirm = () => {
    if (!isMatch) {
      setShowError(true);
      return;
    }
    onConfirm();
  };

  // Prevent paste
  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    if (showError) setShowError(false);
  };

  return (
    <div
      ref={overlayRef}
      className="delete-org-overlay"
      onMouseDown={e => {
        if (!(e.target instanceof Element)) {
          mouseDownInsideContent.current = false;
          return;
        }
        mouseDownInsideContent.current = e.target.closest('.delete-org-modal') !== null;
      }}
      onClick={e => {
        if (e.target === overlayRef.current && !mouseDownInsideContent.current && !loading) {
          onCancel();
        }
      }}
    >
      <div className="delete-org-modal">
        {/* Header */}
        <div className="delete-org-header">
          <div className="delete-org-icon">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M3 6h18" />
              <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
              <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
              <line x1="10" x2="10" y1="11" y2="17" />
              <line x1="14" x2="14" y1="11" y2="17" />
            </svg>
          </div>
          <h3 className="delete-org-title">Delete Organization</h3>
        </div>

        {/* Content */}
        <div className="delete-org-body">
          {/* Risk warning */}
          <div className="delete-org-warning">
            <div className="delete-org-warning-title">
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
                <path d="M12 9v4" />
                <path d="M12 17h.01" />
              </svg>
              This action is irreversible! The following data will be cascade deleted:
            </div>
            <ul className="delete-org-warning-list">
              <li>All Folders under the organization</li>
              <li>All Projects under the organization</li>
              <li>All Organization Member relationships</li>
              <li>All Project Member relationships</li>
            </ul>
          </div>

          {/* Input confirmation */}
          <div className="delete-org-input-group">
            <label className="delete-org-input-label">
              Please type the organization name <strong>{orgName}</strong> below to confirm
              deletion:
            </label>
            <input
              ref={inputRef}
              className={`delete-org-input${showError ? ' input-error' : ''}`}
              type="text"
              placeholder="Enter organization name"
              value={inputValue}
              onChange={handleInputChange}
              onPaste={handlePaste}
              disabled={loading}
              autoComplete="off"
            />
            {showError && (
              <span className="delete-org-input-hint">
                Organization name is incorrect, please try again
              </span>
            )}
          </div>
        </div>

        {/* Footer buttons */}
        <div className="delete-org-footer">
          <button className="delete-org-cancel-btn" onClick={onCancel} disabled={loading}>
            Cancel
          </button>
          <button
            className="delete-org-confirm-btn"
            onClick={handleConfirm}
            disabled={loading || !isMatch}
          >
            {loading && <span className="delete-org-spinner" />}
            {loading ? 'Deleting...' : 'Confirm Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function DeleteOrganizationModal({
  isOpen,
  orgName,
  loading = false,
  onConfirm,
  onCancel,
}: DeleteOrganizationModalProps) {
  if (!isOpen) return null;

  return (
    <DeleteOrganizationModalContent
      key={orgName}
      orgName={orgName}
      loading={loading}
      onConfirm={onConfirm}
      onCancel={onCancel}
    />
  );
}
